import { MoneyData } from '@/models/Money'
import Form from '@/vue/components/Form.vue'
import Step from '@/vue/components/Step.vue'
import { useToast } from '@/vue/composables/useToast'
import { AxiosError, AxiosResponse } from 'axios'
import human from 'human-format'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { DateTime } from 'luxon'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'
import { capitalize } from 'vue'

export const Renderer = {
	date: (value?: string | null, format: string = 'MMMM d, yyyy'): string => (parseToDateTime(value).isValid ? parseToDateTime(value).toFormat(format) : 'N/A'),
	datetime: (value?: string | null, format: string = 'MMMM d, yyyy h:mm a'): string => (parseToDateTime(value).isValid ? parseToDateTime(value).toFormat(format) : 'N/A'),
	bool: (value?: boolean): string => (value ? 'Yes' : 'No'),
	enum: (value: string): string => label(value),
	money: (value: MoneyData | number | string, currency: string = 'NGN'): string => {
		if (typeof value === 'number') {
			return (value / 100)?.toLocaleString('en-NG', { style: 'currency', currency })
		} else if (typeof value === 'string') {
			return (Number(value) / 100)?.toLocaleString('en-NG', { style: 'currency', currency })
		} else if (typeof value === 'object') {
			return value.formatted
		} else {
			return '<i>N/A</i>'
		}
	},
	phone: (value: string): string => parsePhoneNumberFromString(value)?.formatInternational() ?? '<i>N/A</i>',
	capitalize: (value: string): string => capitalize(value),
	label: (value: string): string => label(value),
	number: (value: number, decimals = 1, useWords = false, symbol = 't'): string => metricNumber(value, decimals, useWords, symbol),
	content: (data): string => (data?.ops ? new QuillDeltaToHtmlConverter(data.ops).convert() : ''),
	figure: (value: number | undefined, decimals = 0): string => (value ? value?.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : '0'),
	metric,
}

// Small helper to robustly parse several common date formats into a Luxon DateTime
export function parseToDateTime(value?: string | null): DateTime {
	if (!value) return DateTime.invalid('empty')

	// Try ISO first
	let dt = DateTime.fromISO(value)
	if (dt.isValid) return dt

	// Try RFC2822
	dt = DateTime.fromRFC2822(value)
	if (dt.isValid) return dt

	// Try SQL
	dt = DateTime.fromSQL(value)
	if (dt.isValid) return dt

	// Fallback to JS Date parsing
	const js = new Date(value)
	if (!isNaN(js.getTime())) {
		return DateTime.fromJSDate(js)
	}

	return DateTime.invalid('unparseable')
}

export function metricNumber(value: number, decimals = 1, useWords = false, symbol = 'ton'): string {
	const spacer = ' '
	if (!isFinite(value)) {
		return String(value)
	}

	const negative = value < 0
	let val = Math.abs(value)

	if (val < 1000) {
		// format and trim trailing zeros and optional decimal point
		const smallFormatted = val.toFixed(decimals).replace(/\.?0+$/, '')
		return (negative ? '-' : '') + smallFormatted + symbol
	}

	const units = ['', 'k', 'M', 'G', 'T', 'P', 'E']
	const words = ['', 'kilo', 'mega', 'giga', 'tera', 'peta', 'exa']

	if (units.includes(symbol.charAt(0))) {
		return val + spacer + symbol
	}

	let idx = 0
	while (val >= 1000 && idx < units.length - 1) {
		val = val / 1000
		idx++
	}

	// format with the requested decimals, trim trailing zeros
	const factor = Math.pow(10, decimals)
	const rounded = Math.round(val * factor) / factor
	const formatted = rounded.toFixed(decimals).replace(/\.?0+$/, '')

	const suffix = useWords ? words[idx] : units[idx]

	return (negative ? '-' : '') + formatted + spacer + suffix + symbol
}

export function metric(value: number, unit: string = 'ton', decimals = 1) {
	return human(value, { unit, decimals })
}

export const label = (value: string) =>
	value
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')

export const error = (e: AxiosError<AxiosResponse> | Error, view?: ViewModel, _toast?: boolean) => {
	console.error(e)

	if (view && typeof view.message !== 'undefined') {
		// @ts-ignore
		view.message = e.response?.data?.message
	}

	if (view?.steps) {
		for (let step of view.steps) {
			// @ts-ignore
			step?.form?.setErrors(e.response?.data?.errors)
		}
	}

	if (view?.form) {
		// @ts-ignore
		view.form.setErrors(e.response?.data?.errors)

		const element = document.querySelector('.alert')
		if (element) {
			element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}
	if (e?.response?.status == 422) {
		useToast().warning(e?.response?.data?.message)
	} else {
		useToast().error(e?.response?.data?.message ?? e)
	}
}

interface ViewModel {
	message: string
	form?: InstanceType<typeof Form>
	steps?: InstanceType<typeof Step>[]
}

export interface SelectArray {
	name: string
	value: string
}
